import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { AdminShell, RequireSession, TenantShell } from './components/Shell';
import { Loading } from './components/ui';
import { AuthProvider, homeFor, useAuth } from './lib/auth';
import { AdminActivity } from './pages/admin/Activity';
import { AdminCustomer, AdminCustomers } from './pages/admin/Customers';
import { AdminCustomerSessions } from './pages/admin/CustomerSessions';
import { AdminOverview } from './pages/admin/Overview';
import { AdminStaff } from './pages/admin/Staff';
import { AdminSystem } from './pages/admin/System';
import { AdminBilling } from './pages/admin/Billing';
import { AdminChangelog } from './pages/admin/Changelog';
import { AdminTenants } from './pages/admin/Tenants';
import { Choose, LinkPage, NotHere, NotReady, Setup, SignIn } from './pages/SignIn';
import type { ReactNode } from 'react';
import { TenantAccounts } from './pages/tenant/Accounts';
import { TenantCalls } from './pages/tenant/Calls';
import { MessageHistory, PatchHistory, VoicemailHistory } from './pages/tenant/HistoryArchive';
import { MeetingHistory } from './pages/tenant/MeetingHistory';
import { TenantHistory } from './pages/tenant/History';
import { TenantHome } from './pages/tenant/Home';
import { MyCalls, MyPhone } from './pages/tenant/MyPhone';
import { TenantNumbers } from './pages/tenant/Numbers';
import { GetNumbers } from './pages/tenant/GetNumbers';
import { MoveNumbers } from './pages/tenant/MoveNumbers';
import { TenantEmergency } from './pages/tenant/Emergency';
import { AdminNumbers } from './pages/admin/Numbers';
import { AdminTexting } from './pages/admin/Texting';
import { AdminTests } from './pages/admin/Tests';
import { AdminFieldTests } from './pages/admin/FieldTests';
import { AdminArchiveCompanies } from './pages/admin/ArchiveCompanies';
import { AdminUpdate, AdminUpdates } from './pages/admin/Updates';
import { TenantPeople } from './pages/tenant/People';
import { PhoneEditor, TenantPhones } from './pages/tenant/Phones';
import { TemplateEditor, TenantTemplates } from './pages/tenant/Templates';
import { TenantTeam } from './pages/tenant/Team';
import { TenantVoicemail } from './pages/tenant/Voicemail';
import { TenantWallboard } from './pages/tenant/Wallboard';
import { TenantReports } from './pages/tenant/Reports';
import { BillingInvoice, TenantBilling } from './pages/tenant/Billing';
import { TenantSettings } from './pages/tenant/Settings';
import { TextingRegistrationPage } from './pages/tenant/TextingRegistration';
import { StoragePage } from './pages/tenant/Storage';
import { TenantLegalHolds } from './pages/tenant/LegalHolds';
import { PersonPage } from './pages/tenant/Person';
import { NewPerson } from './pages/tenant/NewPerson';
import { HelpCenter } from './pages/Help';
import { AdminSupport } from './pages/admin/Support';
import { AdminLive } from './pages/admin/Live';
import { AdminRequests } from './pages/admin/Requests';
import { TenantWhatsOn } from './pages/tenant/WhatsOn';
import { LiveViewer } from './pages/LiveViewer';
import { LiveHelpCustomer } from './live/LiveHelp';
// Patches: the floating bar of the Patch someone is in, wherever they go in the app.
import { PatchDock } from './components/PatchDock';
import { CallDock } from './components/CallDock';
import { LiveLinkNote } from './components/LiveLinkNote';
import { LelandPage } from './pages/tenant/LelandPage';
import { TenantActivity } from './pages/tenant/Activity';
import { TenantSetup } from './pages/tenant/Setup';
import { TenantTeams } from './pages/tenant/Teams';
import { SessionReplayPage } from './components/SessionReplay';
import { TvBoard } from './pages/TvBoard';
import { TenantMessages } from './pages/tenant/Messages';
import { PhoneActivity } from './pages/tenant/PhoneActivity';
import { PhoneMore } from './pages/tenant/PhoneMore';
import { MeetingDetail, TenantMeetings } from './pages/tenant/Meetings';
import { ConsolePage } from './pages/tenant/Console';
import { MeetingPage } from './pages/MeetingPage';

/**
 * Stitch Admin's own pages (its menu, live help's viewer, the first-run setup): not in the portal of
 * a split Stitch (STITCH_SURFACE=portal), which says so, with the way to Stitch Admin for staff.
 */
function StaffSide({ children }: { children: ReactNode }) {
  const { state, error } = useAuth();
  if (!state && !error) return <Loading label="Loading" />;
  return state?.surface === 'portal' ? <NotHere /> : <>{children}</>;
}

function Home() {
  const { state } = useAuth();
  if (!state) return <Loading label="Loading" />;
  if (!state.signedIn) return <Navigate to="/sign-in" replace />;
  return <Navigate to={homeFor(state)} replace />;
}

export function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/invite" element={<LinkPage purpose="invite" />} />
          <Route path="/reset" element={<LinkPage purpose="reset" />} />
          <Route
            path="/setup"
            element={
              <StaffSide>
                <Setup />
              </StaffSide>
            }
          />
          <Route path="/choose" element={<Choose />} />
          <Route path="/not-ready" element={<NotReady />} />
          {/* Help is open to everyone, signed in or not. */}
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/help/:id" element={<HelpCenter />} />
          {/* The old development access page. */}
          <Route path="/access" element={<Navigate to="/sign-in" replace />} />
          <Route
            path="/admin"
            element={
              <StaffSide>
                <RequireSession>
                  <AdminShell />
                </RequireSession>
              </StaffSide>
            }
          >
            <Route index element={<AdminOverview />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="customers/:id" element={<AdminCustomer />} />
            <Route path="customers/:id/sessions" element={<AdminCustomerSessions />} />
            <Route path="tenants" element={<AdminTenants />} />
            <Route path="numbers" element={<AdminNumbers />} />
            <Route path="billing" element={<AdminBilling />} />
            <Route path="texting" element={<AdminTexting />} />
            {/* 1.12.33: moved into Updates (each customer's page, and Updates → Voicemail emails). */}
            <Route path="voicemail-emails" element={<Navigate to="/admin/updates/voicemail-emails" replace />} />
            <Route path="updates" element={<AdminUpdates />} />
            <Route path="updates/:id" element={<AdminUpdate />} />
            <Route path="tests" element={<AdminTests />} />
            <Route path="field-tests" element={<AdminFieldTests />} />
            <Route path="archive" element={<AdminArchiveCompanies />} />
            <Route path="activity" element={<AdminActivity />} />
            <Route path="staff" element={<AdminStaff />} />
            <Route path="system" element={<AdminSystem />} />
            <Route path="changelog" element={<AdminChangelog />} />
            <Route path="support" element={<AdminSupport />} />
            <Route path="live" element={<AdminLive />} />
            <Route path="requests" element={<AdminRequests />} />
          </Route>
          <Route
            path="/t/:tenant"
            element={
              <RequireSession>
                <TenantShell />
              </RequireSession>
            }
          >
            <Route index element={<TenantHome />} />
            {/* The admin home again, for the phone app (whose Home is Messages): More → Company admin. */}
            <Route path="overview" element={<TenantHome />} />
            <Route path="setup" element={<TenantSetup />} />
            <Route path="people" element={<TenantPeople />} />
            <Route path="people/new" element={<NewPerson />} />
            <Route path="people/:extension" element={<PersonPage />} />
            <Route path="voicemail" element={<TenantVoicemail />} />
            <Route path="phones" element={<TenantPhones />} />
            <Route path="phones/:mac" element={<PhoneEditor />} />
            <Route path="templates" element={<TenantTemplates />} />
            <Route path="templates/:id" element={<TemplateEditor />} />
            <Route path="numbers" element={<TenantNumbers />} />
            <Route path="numbers/new" element={<GetNumbers />} />
            <Route path="numbers/move" element={<MoveNumbers />} />
            <Route path="emergency" element={<TenantEmergency />} />
            <Route path="team" element={<TenantTeam />} />
            <Route path="settings" element={<TenantSettings />} />
            <Route path="settings/texting" element={<TextingRegistrationPage />} />
            <Route path="settings/storage" element={<StoragePage />} />
            <Route path="legal-holds" element={<TenantLegalHolds />} />
            <Route path="whats-on" element={<TenantWhatsOn />} />
            <Route path="view/:extension" element={<MyPhone />} />
            <Route path="me" element={<MyPhone />} />
            <Route path="me/calls" element={<MyCalls />} />
            <Route path="ring-groups" element={<TenantAccounts key="ringGroup" kind="ringGroup" />} />
            <Route path="auto-attendants" element={<TenantAccounts key="autoAttendant" kind="autoAttendant" />} />
            <Route path="time-rules" element={<TenantAccounts key="timeRule" kind="timeRule" />} />
            <Route path="call-park" element={<TenantAccounts key="callPark" kind="callPark" />} />
            <Route path="queues" element={<TenantAccounts key="callQueue" kind="callQueue" />} />
            <Route path="wallboard" element={<TenantWallboard />} />
            <Route path="reports" element={<TenantReports />} />
            <Route path="billing" element={<TenantBilling />} />
            <Route path="billing/invoices/:id" element={<BillingInvoice />} />
            <Route path="calls" element={<TenantCalls />} />
            <Route path="calls/voicemail" element={<VoicemailHistory />} />
            <Route path="calls/messages" element={<MessageHistory />} />
            <Route path="calls/patches" element={<PatchHistory />} />
            <Route path="calls/meetings" element={<MeetingHistory />} />
            <Route path="history" element={<TenantHistory />} />
            <Route path="messages" element={<TenantMessages />} />
            <Route path="messages/:conversation" element={<TenantMessages />} />
            {/* The phone app's other tabs (components/PhoneApp). */}
            <Route path="dms" element={<TenantMessages view="dms" />} />
            <Route path="meetings" element={<TenantMeetings />} />
            <Route path="meetings/:id" element={<MeetingDetail />} />
            <Route path="console" element={<ConsolePage />} />
            <Route path="feed" element={<PhoneActivity />} />
            <Route path="more" element={<PhoneMore />} />
            <Route path="leland" element={<LelandPage />} />
            <Route path="activity" element={<TenantActivity />} />
            <Route path="teams" element={<TenantTeams />} />
          </Route>
          {/* Live help's viewer: its own tab, loaded fresh (the page copy needs its own style rules). */}
          <Route
            path="/live/:id"
            element={
              <StaffSide>
                <RequireSession>
                  <LiveViewer />
                </RequireSession>
              </StaffSide>
            }
          />
          {/* A wallboard on a TV: its own window, its own read-only pass, no sign-in. */}
          {/* A meeting's link (1.13.0): anyone, signed in or not; its own page, outside the menu. */}
          <Route path="/m/:code" element={<MeetingPage />} />
          <Route path="/tv" element={<TvBoard />} />
          <Route path="/tv/:id" element={<TvBoard />} />
          {/* A session recording, played back in its own tab (loaded fresh, like live help's viewer). */}
          <Route
            path="/replay/:tenant/:id"
            element={
              <RequireSession>
                <SessionReplayPage />
              </RequireSession>
            }
          />
          <Route path="*" element={<Home />} />
        </Routes>
        <LiveHelpCustomer />
        {/* Patches: stays connected (and on screen) while the person moves around the app. */}
        <PatchDock />
        {/* Calls in the app: ringing, the call on this device, and what happened to one that ended. */}
        <CallDock />
        {/* "Reconnecting…" while the live connection is down. */}
        <LiveLinkNote />
      </BrowserRouter>
    </AuthProvider>
  );
}
